import { Combobox } from "@/components/Combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { COUNTRIES } from "@/lib/countries";
import { cn } from "@/lib/utils";
import { TripLocationValue } from "@/lib/trip";
import { AllowedCountry, TravelType } from "../../types";

// Kept in step with requests_api.py's MAX_CITY_LENGTH.
const MAX_CITY_LENGTH = 100;

const ALL_COUNTRY_OPTIONS = COUNTRIES.map((c) => ({
  value: c.name,
  label: c.name
}));

// Domestic: pick the country, then a start city and an end city within it.
// International: pick the starting country and the destination country.
// Either way the policy country (and so the currency, shown read-only) comes
// from `allowedCountries` - only countries an admin has configured for this
// travel type, so a submission can't fail with "not configured". The
// starting country of an International trip has no policy behind it, so it
// offers the full world list.
// Shared by the New Request wizard and the Request Advance form.
export function TripLocationFields({
  travelType,
  value,
  onChange,
  allowedCountries,
  loading,
  emptyHint,
  idPrefix,
  showCurrency = true
}: {
  travelType: TravelType;
  value: TripLocationValue;
  onChange: (value: TripLocationValue) => void;
  allowedCountries: AllowedCountry[];
  loading: boolean;
  /** Shown when nothing is configured for this travel type yet. */
  emptyHint: string;
  idPrefix: string;
  /** The read-only Currency box beside the country. Off where the form
   *  shows currencies itself (the Request Advance form's per-currency
   *  amount rows), so the currency isn't shown twice. */
  showCurrency?: boolean;
}) {
  const currency =
    allowedCountries.find((c) => c.country === value.country)?.currency || "";
  const set = (patch: Partial<TripLocationValue>) =>
    onChange({ ...value, ...patch });
  const isDomestic = travelType === "domestic";

  // An International trip can't start and end in the same country - each
  // picker leaves the other's choice out so that combination can't be made.
  const destinationOptions = allowedCountries
    .filter((c) => isDomestic || c.country !== value.originCountry)
    .map((c) => ({ value: c.country, label: c.country }));
  const originOptions = ALL_COUNTRY_OPTIONS.filter(
    (o) => o.value !== value.country
  );

  // One row: [Starting country] (International only) | [Country /
  // Destination country] | [Currency] (when shown). items-end keeps the
  // fields level when a longer label wraps (larger text size, narrow card).
  const columns = cn(
    "grid items-end gap-3",
    !isDomestic && showCurrency && "sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_7rem]",
    !isDomestic && !showCurrency && "sm:grid-cols-2",
    isDomestic && showCurrency && "sm:grid-cols-[minmax(0,1fr)_7rem]"
  );

  return (
    <div className="space-y-3">
      <div className={columns}>
        {!isDomestic && (
          <div className="space-y-1.5">
            <Label>Starting country</Label>
            <Combobox
              value={value.originCountry}
              onChange={(originCountry) => set({ originCountry })}
              options={originOptions}
              placeholder="Select starting country"
              searchPlaceholder="Search countries…"
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>{isDomestic ? "Country" : "Final destination country"}</Label>
          <Combobox
            value={value.country}
            onChange={(country) => set({ country })}
            options={destinationOptions}
            placeholder={
              loading
                ? "Loading…"
                : isDomestic
                  ? "Select country"
                  : "Select destination country"
            }
            searchPlaceholder="Search countries…"
            disabled={loading}
          />
        </div>
        {showCurrency && (
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <Input value={currency || "-"} disabled readOnly />
          </div>
        )}
      </div>

      {isDomestic && (
        <div className="grid items-end gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-start-city`}>Start city</Label>
            <Input
              id={`${idPrefix}-start-city`}
              value={value.startCity}
              onChange={(e) => set({ startCity: e.target.value })}
              maxLength={MAX_CITY_LENGTH}
              placeholder="City the trip starts from"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-end-city`}>End city</Label>
            <Input
              id={`${idPrefix}-end-city`}
              value={value.endCity}
              onChange={(e) => set({ endCity: e.target.value })}
              maxLength={MAX_CITY_LENGTH}
              placeholder="City the trip ends in"
            />
          </div>
        </div>
      )}

      {!loading && allowedCountries.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          {emptyHint}
        </p>
      )}
      <p className="text-xs text-amber-600 dark:text-amber-400">
        If your {isDomestic ? "country" : "destination"} is not listed here,
        please contact your admin for configuration.
      </p>
    </div>
  );
}
